import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";

import { HoverHintContent } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import {
  DEFAULT_SCHEDULER_LIVENESS,
  INGEST_CONTROL_QUERY_LIVE,
  type IngestControlListResponse,
  type SchedulerLiveness,
} from "@/lib/ingest-control-api";
import { useErpIngestLiveQueries } from "@/lib/useErpIngestLiveQueries";
import { relativeTime } from "@/lib/utils";

/**
 * One global indicator: the `npm run scheduler` / `dist/scheduler.js` process,
 * not per-entity enable flags. Uses the same query as the dashboard
 * (GET /api/ingest-control) so a separate endpoint cannot 404 in local dev.
 */
export function SchedulerStatus() {
  useErpIngestLiveQueries();
  const query = useQuery({
    queryKey: ["ingest-control"],
    queryFn: () => apiFetch<IngestControlListResponse>("/api/ingest-control"),
    ...INGEST_CONTROL_QUERY_LIVE,
    select: (data: IngestControlListResponse): SchedulerLiveness =>
      data.scheduler ?? DEFAULT_SCHEDULER_LIVENESS,
  });

  if (query.isLoading) {
    return (
      <HoverHintContent hoverHint="Loading ingest status…" className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Activity className="h-3.5 w-3.5" />
        Scheduler…
      </HoverHintContent>
    );
  }

  if (query.isError) {
    const err = query.error;
    const status = err instanceof ApiError ? err.status : 0;
    const message = err instanceof Error ? err.message : "Request failed";
    const hintText =
      status === 401
        ? "Not signed in or session expired. Sign in to load ingest status and scheduler heartbeat."
        : `Cannot load /api/ingest-control: ${message}. The scheduler is almost certainly not running the UI until this succeeds. In local development, the integrator API on port 8123 must serve this endpoint.`;
    return (
      <HoverHintContent hoverHint={hintText} className="inline-flex max-w-[11rem] flex-col items-end sm:max-w-sm">
        <span className="inline-flex items-center gap-1">
          <Activity className="h-3.5 w-3.5 text-amber-600" />
          <Badge tone="warning" className="whitespace-nowrap">
            Scheduler off
          </Badge>
        </span>
        <span className="text-[10px] leading-tight text-slate-500">
          {status === 401 ? "Sign in to load status" : "API unreachable"}
        </span>
      </HoverHintContent>
    );
  }

  const data = query.data;
  if (!data) {
    return null;
  }

  const detail =
    data.lastSeenAt == null
      ? "The scheduler has not heartbeated yet. The cron loop runs in a separate scheduler process; the HTTP API does not emit scheduler heartbeats."
      : `Last process heartbeat: ${data.lastSeenAt}. Tick: every ${data.expectedTickSeconds} seconds.`;

  if (data.ok) {
    return (
      <HoverHintContent hoverHint={detail} className="inline-flex items-center gap-1">
        <Activity className="h-3.5 w-3.5 text-green-600" />
        <Badge tone="success" className="max-w-[14rem] truncate sm:max-w-none">
          Scheduler on
        </Badge>
      </HoverHintContent>
    );
  }

  return (
    <HoverHintContent hoverHint={detail} className="inline-flex max-w-xs flex-col items-end gap-0.5 sm:max-w-md">
      <span className="inline-flex items-center gap-1">
        <Activity className="h-3.5 w-3.5 text-amber-600" />
        <Badge tone="warning" className="whitespace-nowrap">
          Scheduler off
        </Badge>
      </span>
      {data.lastSeenAt ? (
        <span className="text-[10px] text-slate-500">Last seen {relativeTime(data.lastSeenAt)}</span>
      ) : null}
    </HoverHintContent>
  );
}
